import AppPopup from '@/components/popups/app-popup';
import EventBus from '@/scripts/event-bus';
import BoardUtility from '@/scripts/board-utility';
import { LandingPageService } from '@/scripts/services/landing-page/landing-page-service';
import { LandingPageStatsService } from '@/scripts/services/landing-page/landing-page-stats-service';

const duplicateLandingPageErrorMessage = 'A landing page with the same name already exists in your account';

export const LandingPageBoardServiceMixin = {

    data() {
        return {
            selectedItems: [],
            deletingItems: [],
        };
    },

    computed: {
        accountId() {
            return this.$store.state.context.account.id;
        },
    },

    methods: {
        landingPagesMetrics(landingPages) {
            return new Promise((resolve, reject) => {
                if(landingPages.value.length > 0){
                    const landingPageIds = landingPages.value.map((landingPage) => landingPage.Id);

                    LandingPageStatsService.post(landingPageIds)
                    .then(({ data }) => resolve({
                        landingPages: landingPages.value,
                        metrics: data,
                        count: landingPages['@odata.count']
                    }))
                    .catch(reject)
                }
                else {
                    resolve({
                        landingPages: [],
                        metrics: null,
                        count: 0
                    })
                }

            })
        },

        refreshEvent() {
            EventBus.$emit('landing-page:update');
        },

        onOverview({ id }) {
            this.$router.push(`/LandingPages/${id}/Details`);
        },

        duplicateItem({ id, landingPage: { name } }) {
            const widget = {
                name: 'duplicate-widget',
                entityId: id,
                entityName: name,
                serviceName: 'LandingPages',
                entityType: 'Landing Page',
                fieldLabel: 'Choose a name for the new landing page',
                fieldPlaceholder: 'LP Name',
                replaceEntityField: 'Name',
                onSuccess: () => {
                    this.refreshEvent();
                },
                onError: (error) => {
                    this.duplicateErrorCheck(error) 
                }
            };
            
            EventBus.$emit('open-widget', widget);
        },

        duplicateErrorCheck(error) {

            if(error.response.data.Message === duplicateLandingPageErrorMessage) {
                this.$helpers.showFailNotification(
                    `A landing page with the same name already exists in your account`,
                    { error },
                )
            } else { this.$helpers.showFailNotification(
                `An error occurred trying to duplicate the Landing Page`,
                { error },
                );
            }
               
        },

        editItem({ id }) {
            const widget = {
                name: 'landing-page-settings-widget',
                landingPageId: id,
            };

            EventBus.$emit('open-widget', widget);
        },

        onPublish({ id }) {

            LandingPageService.publish(id)
                .then(() => {
                    this.$helpers.showSuccessNotification('Landing page published successfully.');

                    this.refreshEvent();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to publish the landing page.', { error });
                });

        },

        onUnpublish({ id }) {

            LandingPageService.unpublish(id)
                .then(() => {
                    this.$helpers.showSuccessNotification('Landing page unpublished successfully.');

                    this.refreshEvent();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to unpublish the landing page.', { error });
                });

        },

        deleteItem(item) {
            this.deletingItems = [item];
            LandingPageService.delete(item.id)
                .then(() => {
                    this.$helpers.showSuccessNotification('Landing page deleted successfully.');
                    this.refreshEvent();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred trying to delete the landing page.', { error });
                    this.deletingItems = [];
                });
        },

        deleteItems(items) {
            this.deletingItems = [...items];

            const ids = this.deletingItems.map(({ id }) => id);

            LandingPageService.deleteAll(ids)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        `${ids.length > 1 ? `${ids.length} landing pages have` : '1 landing page has'} been deleted.`
                    );
                    this.deletingItems = [];
                    this.refreshEvent();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        `An error occurred while deleting the selected landing page${ids.length > 1 ? 's' : ''}`,
                        { error }
                    );
                });
        },

        // Factories

        createDeleteLandingPageModal(item, confirmFunction) {
            return () => {
                const actions = {
                    confirm: {
                        label: 'Delete Landing Page',
                        action: () => confirmFunction(item),
                    },

                    cancel: {
                        label: 'Cancel',
                    },
                };

                const content = '<p>All information associated with the landing page will be deleted. You can not undo this action.</p>';

                this.$modal.show(
                    AppPopup,
                    {
                        type: 'alert-popup',
                        title: 'Are you sure you want to delete this landing page?',
                        content,
                        actions,
                        confirmText: 'Delete',
                    },
                    {
                        height: this.$helpers.setPopupHeight(),
                        width: this.$helpers.setPopupWidth(),
                    }
                );
            };
        },

        createDeleteMultipleLandingPageModal(confirmFunction) {
            return () => {
                const actions = {
                    confirm: {
                        label: 'Delete Landing Pages',
                        action: confirmFunction,
                    },

                    cancel: {
                        label: 'Cancel',
                    },
                };

                const content = '<p>All information associated with these landing pages will be deleted. You can not undo this action.</p>';

                this.$modal.show(
                    AppPopup,
                    {
                        type: 'text-confirm-popup',
                        title: 'Are you sure?',
                        content,
                        actions,
                        confirmText: 'Delete',
                    },
                    {
                        height: this.$helpers.setPopupHeight(),
                        width: this.$helpers.setPopupWidth(),
                    }
                );
            };
        },

        // Table mapper

        landingPageItems(landingPages, metrics){
            const landingPageMetrics = (landingPage) => {
                const metric = metrics.find((metric) => metric.LandingPageId === landingPage.Id)
                return{
                    formSubmissions: landingPage.LandingPageFormSubmissions.length,
                    uniqueViews: metric ? metric.PageViews : 0,
                }
            };

            const items = landingPages.map((item) => {
                return {
                    id: item.Id,
                    landingPage: {
                        id: item.Id,
                        name: item.Name,
                        thumbnailImageUrl: item.PreviewImageUrl || null,
                        url: `https://${item.Domain.DomainName}/${item.Slug}`,
                    },
                    formSubmissions: {
                        link: `/LandingPages/${item.Id}/Forms/Submissions`,
                        label: landingPageMetrics(item).formSubmissions,
                    },
                    categories: item.Categories.map(({ Name }) => ({name: Name})),
                    uniqueViews: landingPageMetrics(item).uniqueViews,
                    updatedDate: this.$moment(item.UpdatedAt),
                    createdDate: this.$moment(item.CreatedAt)
                }
            })

            return items;
        },

        // Query methods

        getFilterParam(options, status) {
            const queryFilters = [];

            if (status != null) {
                const typeFilter = `Status eq TriDigital.EntityDomain.Entities.LandingPageStatus'${status}'`;
                queryFilters.push(typeFilter);
            }

            if (options.filters) {
                const viewQueryFilter = this.getViewQueryFilter(options.filters);
                if (viewQueryFilter) {
                    queryFilters.push(viewQueryFilter);
                }

                if (options.search) {
                    queryFilters.push(this.getSearchFilter(options.search));
                }
            }

            if (this.accountId) {
                queryFilters.push(`AccountId eq ${this.accountId}`);
            }

            return queryFilters.length > 0 ? `${queryFilters.join(' and ')}` : null;
        },

        getViewQueryFilter(filters) {
            if (filters.value.length > 0) {
                const viewQueryFilters = filters.value.map(this.mapFilter);

                if (filters.operation === 'Or') {
                    return `(${viewQueryFilters.join(' or ')})`;
                } else {
                    return `(${viewQueryFilters.join(' and ')})`;
                }
            }

            return null;
        },

        mapFilter(filter) {
            switch (filter.key) {
                case 'landingPage':
                    return BoardUtility.getLandingPageNameFilter(filter);
                case 'published-url':
                    return BoardUtility.getLandingPageUrlFilter(filter);
                case 'categories':
                    return BoardUtility.getLandingPageCategoryFilter(filter);
                default:
                    return null;
            }
        },

        getSearchFilter(search) {
            const encodedSearch = encodeURIComponent(search);

            return `(contains(Name, '${encodedSearch}'))`;
        },

        getPageParam(options) {
            return options.itemsPerPage && options.page ? `${options.itemsPerPage * (options.page - 1)}` : null;
        },

        getTopParam(options) {
            return options.itemsPerPage ? `${options.itemsPerPage}` : null;
        },

        getOrderParam(options) {
            return options.sort ? `${options.sort.property} ${options.sort.direction}` : null;
        },

        createParamsFromBoardQuery(options, statusType) {
            return {
                filter: this.getFilterParam(options, statusType),
                skip: this.getPageParam(options),
                top: this.getTopParam(options),
                orderBy: this.getOrderParam(options),
                expand: 'Categories($select=Name),Domain,LandingPageFormSubmissions',
                count: true,
            };
        },

        // Get landing pages

        dispatchGetLandingPages(status, options = {}) {

            const requestConfig = this.createParamsFromBoardQuery(options, status);

            return new Promise((resolve, reject) => {
                LandingPageService.getAll(requestConfig)
                    .then(({ data }) => this.landingPagesMetrics(data))
                    .then(({ landingPages, metrics, count }) => resolve({
                            items: this.landingPageItems(landingPages, metrics),
                            count: count,
                    }))
                    .catch(reject);
            });
        },
    },
};
